import "@sphtech/dmg-design-system/assets/nuyou/fonts.css";
import "@sphtech/dmg-design-system/dist/theme-nuyou.css";

import { extractMagazineIssues } from "@app/utils/extractMagazineIssue";
import Newsticker from "@components/Modules/Newsticker/Newsticker";
import { configGAM, configSite } from "@pub/config";
import {
  BaseLayout,
  CustomClassContext,
  useBaseContext,
} from "@sphtech/dmg-design-system";
import { TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { useLocation } from "react-router";

import MagNewsletter from "./components/MagNewsletter";
import NavbarNUYOU from "./components/NavbarNUYOU";
import { configHeader } from "./config/configHeader";
import customStyle from "./custom.module.css";
import logoSvgSource from "./icons/logo.svg?raw";
import logoSvgInverted from "./icons/logo-inverted.svg?raw";
import { RoutePaths } from "./routePaths";

type NuyouBaseLayoutProps = {
  response?: TRouteWithRedirect<unknown, unknown>;
  ArticleTitle?: string;
  children: React.ReactNode;
  ads?: boolean;
  leaderboard?: boolean;
  skipMobileBanner?: boolean;
  hasMagNewsletter?: boolean;
};

/**
 * Reusable Base layout specific to NUYOU
 */
const NuyouBaseLayout = ({
  ArticleTitle,
  children,
  ads = true,
  leaderboard = true,
  skipMobileBanner = true,
  hasMagNewsletter = true,
  response,
}: NuyouBaseLayoutProps) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const globalData = useBaseContext();
  const { magazineIssues } = globalData || {};

  const pagesWithMagNewsletter = ["/about-us", "/search"];
  const resolvedMagazineIssues =
    hasMagNewsletter &&
    (pagesWithMagNewsletter.includes(location.pathname) ||
      (response && "statusCode" in response && response.statusCode === 404)) &&
    extractMagazineIssues(magazineIssues);

  return (
    <CustomClassContext.Provider
      value={{
        button: {
          primary: customStyle["nuyou-button-primary"],
          secondary: customStyle["nuyou-button-secondary"],
          tertiary: customStyle["nuyou-button-tertiary"],
        },
        videoOverlay: {
          playIcon: customStyle["nuyou-play-icon"],
        },
        heading: {
          headingSplit: customStyle["nuyou-heading-split"],
        },
        tags: {
          primary: customStyle["nuyou-tags-primary"],
          secondary: customStyle["nuyou-tags-secondary"],
        },
        aboutUs: {
          storylineContainer: customStyle["about-us-story-line-container"],
          authorBlockContainer: customStyle["about-us-author-block-container"],
          headingTextContainer: customStyle["about-us-heading-container"],
        },
        authorProfile: {
          link: customStyle["profile-author-link"],
        },
        socialIcons: {
          socialMedialink: customStyle["social-link"],
        },
        socialMedia: {
          altContainer: customStyle["social-media-container"],
        },
        singlePost: {
          readmore: customStyle["nuyou-story-read-more"],
        },
      }}
    >
      <BaseLayout
        ArticleTitle={ArticleTitle}
        ads={ads}
        leaderboard={leaderboard}
        topLeaderboard={configGAM.useTopLeaderboard}
        NewsTicker={<Newsticker />}
        RoutePaths={RoutePaths}
        svgSource={logoSvgSource}
        siteName={configSite.name}
        mciText={
          <span suppressHydrationWarning>
            MDDI (P) 041/09/2025. Published by SPH Media Limited, Co. Regn. No.
            202120748H. Copyright © {currentYear} SPH Media Limited. All rights
            reserved.
          </span>
        }
        socialIconProps={configHeader.socialMedia}
        megaMenuProps={{
          NewsLetterProps: configHeader.megaMenu.newsletter,
          CloseButtonProps: configHeader.megaMenu.closeButton,
          menuType: "accordion",
        }}
        skipMobileBanner={skipMobileBanner}
        currentLocation={location}
        NavbarComponent={NavbarNUYOU}
        footerWithDivider={false}
        footerWithSocialIcons={false}
        sphLogoInverted={true}
        footerSvgSource={logoSvgInverted}
        stickyElement={configSite.stickyElement}
      >
        {children}
        {resolvedMagazineIssues && (
          <MagNewsletter data={{ magazineIssues: resolvedMagazineIssues }} />
        )}
      </BaseLayout>
    </CustomClassContext.Provider>
  );
};

export default NuyouBaseLayout;
